/**
 * EmailOfflineBanner - "we cannot send emails right now"
 * -------------------------------------------------------
 * Shown to everybody, signed in or out, while a moderator has the email
 * switch on. See backend/routes/settings.js for what that switch does.
 *
 * WHY A BANNER AS WELL AS THE REFUSAL MESSAGE.
 *
 * The refusal is what somebody gets when they try; this is what stops them
 * trying. They are worth having separately, because the moment somebody most
 * needs to know is BEFORE they type their email into a password-reset form
 * and start waiting.
 *
 * WHY IT IS SHOWN TO SIGNED-OUT VISITORS TOO. They are the ones it is most
 * for. Somebody who cannot log in is exactly the person about to ask for a
 * reset, and a banner only logged-in people can see would reach everybody
 * except them.
 *
 * WHAT IT DELIBERATELY DOES NOT SAY: why, how many are left, or when it
 * started. "We cannot send email right now, and it is usually back within a
 * day" is what a person needs. Anything more is an invitation to work out
 * what else this site runs out of.
 *
 * It is not dismissable. A banner somebody can close is a banner they close
 * and then forget thirty seconds before it would have saved them.
 */

import { useEffect, useState } from 'react';
import { MailWarning } from 'lucide-react';

import { settingsApi } from '../services/api';
import { useI18n } from '../hooks/useI18n';

export default function EmailOfflineBanner() {
  const { t } = useI18n();
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let alive = true;

    const check = () => {
      settingsApi
        .read()
        .then((d) => {
          if (alive) setOffline(Boolean(d.emailsOffline));
        })
        // A failure here means we could not ask. Say nothing rather than
        // claiming email is broken on the strength of one failed request -
        // a false alarm on this banner tells everybody the site is half
        // down.
        .catch(() => {});
    };

    check();
    // Slow on purpose. This changes a couple of times a year, and the point
    // of re-checking at all is that somebody sitting on a page when it is
    // switched back on stops being told the wrong thing.
    const timer = setInterval(check, 60000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  if (!offline) return null;

  return (
    <div className="alert alert-warning" style={{ borderRadius: 0, margin: 0 }}>
      <MailWarning size={18} aria-hidden="true" />
      <div className="text-sm">{t('emailOffline.banner')}</div>
    </div>
  );
}
